import React, { useEffect, useMemo, useState } from "react";
import { Group, Modal, Select, Stack, Switch, Text, TextInput } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { AccessUser, OrganisationNode, Role, Salutation, UserCreate } from "@models/access";
import { ApiError } from "@models/error";
import { accessApi } from "@services/accessApi";
import { errorMessage } from "@hooks/monitoring/useApiResource";
import { useEscalationGuard } from "@hooks/access";
import { organisationOptions } from "./organisations";

interface CreateUserModalProps {
  opened: boolean;
  roots: OrganisationNode[];
  roles: Role[];
  defaultOrganisationId: string | null;
  onClose: () => void;
  /** After a create (also a partial one): reload lists, optionally open the user. */
  onCreated: (user: AccessUser) => void;
  /** The email exists already: find that user instead. */
  onFindExisting: (email: string) => void;
}

export const LANGUAGES = ["en", "de", "fr", "it", "es", "nl", "pl", "cs"] as const;
const SALUTATIONS: Salutation[] = ["Mr", "Ms", "None"];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Create a BeeHive user and give them a role for this application in one
 * organisation of the scope. BeeHive does that in two calls; when only the
 * first succeeds core answers 207 and the modal stays open to retry the access.
 */
const CreateUserModal: React.FC<CreateUserModalProps> = ({
  opened, roots, roles, defaultOrganisationId, onClose, onCreated, onFindExisting,
}) => {
  const { t } = useTranslation();
  const { canGrantRole } = useEscalationGuard();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [salutation, setSalutation] = useState<Salutation>("None");
  const [language, setLanguage] = useState<string>("en");
  const [organisationId, setOrganisationId] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ code: string | null; message: string } | null>(null);
  /** Created, but the access assignment failed (207). */
  const [partial, setPartial] = useState<{ user: AccessUser; message: string } | null>(null);

  useEffect(() => {
    if (!opened) return;
    setEmail("");
    setFirstName("");
    setLastName("");
    setSalutation("None");
    setLanguage("en");
    setOrganisationId(defaultOrganisationId);
    setRoleId(null);
    setIsOrgAdmin(false);
    setError(null);
    setPartial(null);
  }, [opened, defaultOrganisationId]);

  const roleOptions = useMemo(
    () => roles.map((role) => ({
      value: role.id,
      label: canGrantRole(role) ? role.name : `${role.name} (${t("admin.access.users.create.roleTooStrong")})`,
      disabled: !canGrantRole(role),
    })),
    [roles, canGrantRole, t],
  );

  const emailInvalid = email.trim() !== "" && !EMAIL.test(email.trim());
  const incomplete = !EMAIL.test(email.trim()) || !firstName.trim() || !lastName.trim() || !organisationId || !roleId;

  const submit = async () => {
    if (incomplete) return;
    const body: UserCreate = {
      email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(), salutation,
      preferredLanguage: language, organisationId: organisationId!, isOrganisationAdmin: isOrgAdmin, roleId: roleId!,
    };
    setSaving(true);
    setError(null);
    try {
      const result = await accessApi().createUser(body);
      onCreated(result.user);
      if (result.accessError) {
        setPartial({ user: result.user, message: result.accessError.message });
      } else {
        onClose();
      }
    } catch (err) {
      setError({ code: err instanceof ApiError ? err.errorCode : null, message: errorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const retryAccess = async () => {
    if (!partial) return;
    setSaving(true);
    try {
      await accessApi().updateAccess(partial.user.id, { organisationId: organisationId!, roleId, isGranted: true });
      onCreated(partial.user);
      onClose();
    } catch (err) {
      setPartial({ ...partial, message: errorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("admin.access.users.create.title")}
      size="lg"
      portalProps={{ target: "#admin-portal-root" }}
    >
      <Stack gap="md">
        <TextInput
          label={t("admin.access.users.create.email")}
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          error={emailInvalid ? t("admin.access.users.create.emailInvalid") : undefined}
          disabled={partial !== null}
          required
        />
        <Group grow gap="md">
          <TextInput label={t("admin.access.users.create.firstName")} value={firstName}
                     onChange={(e) => setFirstName(e.currentTarget.value)} disabled={partial !== null} required />
          <TextInput label={t("admin.access.users.create.lastName")} value={lastName}
                     onChange={(e) => setLastName(e.currentTarget.value)} disabled={partial !== null} required />
        </Group>
        <Group grow gap="md">
          <Select
            label={t("admin.access.users.create.salutation")}
            data={SALUTATIONS.map((s) => ({ value: s, label: t(`admin.access.users.salutation.${s}`) }))}
            value={salutation}
            onChange={(v) => setSalutation((v as Salutation) ?? "None")}
            allowDeselect={false}
            disabled={partial !== null}
            comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
          />
          <Select
            label={t("admin.access.users.create.language")}
            data={LANGUAGES.map((l) => ({ value: l, label: l.toUpperCase() }))}
            value={language}
            onChange={(v) => setLanguage(v ?? "en")}
            allowDeselect={false}
            disabled={partial !== null}
            comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
          />
        </Group>
        <Select
          label={t("admin.access.users.create.organisation")}
          data={organisationOptions(roots, t("admin.access.organisations.inactive"))}
          value={organisationId}
          onChange={setOrganisationId}
          required
          comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
        />
        <Select
          label={t("admin.access.users.create.role")}
          description={t("admin.access.users.create.roleHint")}
          data={roleOptions}
          value={roleId}
          onChange={setRoleId}
          required
          comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
        />
        <Switch
          label={t("admin.access.users.create.orgAdmin")}
          description={t("admin.access.users.create.orgAdminHint")}
          checked={isOrgAdmin}
          onChange={(e) => setIsOrgAdmin(e.currentTarget.checked)}
          disabled={partial !== null}
        />

        {error && (
          <Alert color="red">
            <Stack gap="xs">
              <Text size="sm">
                {error.code === "USER_EXISTS" ? t("admin.access.users.create.exists") : error.message}
              </Text>
              {error.code === "USER_EXISTS" && (
                <Group>
                  <Button size="xs" variant="light" onClick={() => onFindExisting(email.trim())}>
                    {t("admin.access.users.create.findExisting")}
                  </Button>
                </Group>
              )}
            </Stack>
          </Alert>
        )}
        {partial && (
          <Alert color="orange" title={t("admin.access.users.create.partialTitle")}>
            {t("admin.access.users.create.partial", { message: partial.message })}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          {partial ? (
            <Button onClick={retryAccess} loading={saving} disabled={!roleId || !organisationId}>
              {t("admin.access.users.create.retryAccess")}
            </Button>
          ) : (
            <Button onClick={submit} loading={saving} disabled={incomplete}>
              {t("admin.access.users.create.submit")}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

export default CreateUserModal;
